import React, { useContext, useState } from 'react'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { Container } from '@mui/system'
import { Alert, Typography } from '@mui/material'
import {
  calculateDenominationValues,
  GiftCardDenomination,
  PIGGY_BANKS_MULTIPLIER,
} from '@playback-rewards/shared-libraries'

//constants
import { ERROR_TEXT, LOCAL_STORAGE } from 'constants/index'
//components
import ConfirmOrderModal from 'components/ConfirmOrderModal'
import ValueFrame from 'components/valueFrame'
//contexts
import AuthContext from 'context/AuthContext'
import LoaderContext from 'context/LoaderContext'
//hooks
import useLocalStorage from 'hooks/useLocalStorage'
import useOrderGiftCardByCampaignMutation from 'hooks/useOrderGiftCardByCampaignMutation'
//styles
import './index.css'
//types
import { GiftCardListContentWithCampaignProps } from 'types'
//utils
import { formatNumber, isUserFrozen, isUserOutOfAttempts, isUserVerified } from 'utils/utils'

const ContentWithCampaign = ({ campaign, timeLimit }: GiftCardListContentWithCampaignProps) => {
  const { user } = useContext(AuthContext)
  const { showLoader, hideLoader } = useContext(LoaderContext)

  const [showModal, setShowModal] = useState<boolean>(false)
  const [denomination, setDenomination] = useState<string>()
  const [piggyBanksValue, setPiggyBanksValue] = useState<number>(0)

  const navigate = useNavigate()
  const { setItem } = useLocalStorage()

  const { orderGiftCardMutate } = useOrderGiftCardByCampaignMutation()

  const showConfirmOrderModal = (denomination: number) => {
    const { piggyBanksValue } = calculateDenominationValues(denomination)

    setShowModal(true)
    setDenomination(String(denomination))
    setPiggyBanksValue(piggyBanksValue)
  }
  const hideConfirmOrderModal = () => setShowModal(false)

  const handleOrderConfirm = () => {
    const isVerified = isUserVerified(user)
    const outOfAttempts = isUserOutOfAttempts(user)
    const isFrozen = isUserFrozen(user)
    hideConfirmOrderModal()
    if (isVerified) {
      hideConfirmOrderModal()
      showLoader()
      if (isFrozen) {
        hideLoader()
        toast.error(ERROR_TEXT.account_frozen_gift_card, { duration: 10000 })
        return
      }
      if (!denomination) {
        toast.error('Invalid Gift Card denomination')
        return
      }
      return orderGiftCardMutate({ campaignId: campaign.id, denomination })
    }

    if (outOfAttempts) {
      navigate('/verification/result')
      return
    }

    setItem(LOCAL_STORAGE.TREMENDOUS_CAMPAIGN_ID, `${campaign.id}`)
    setItem(LOCAL_STORAGE.GIFT_CARD_DENOMINATION, `${denomination}`)
    navigate('/verification')
  }

  return (
    <>
      <ConfirmOrderModal
        showModal={showModal}
        hideModal={hideConfirmOrderModal}
        denomination={denomination}
        formattedPiggiesValue={formatNumber(piggyBanksValue)}
        piggyBanksValue={piggyBanksValue}
        timeLimit={timeLimit}
        handleOrderConfirm={handleOrderConfirm}
      />
      <Container maxWidth="sm" sx={styles.container}>
        <div className="campaignInner">
          <img src="/images/giftCardPig.png" style={styles.image} />
          <div className="titleContainer">
            <Typography variant="h1">Redeem Rewards</Typography>
            <Typography variant="body1">
              Spend <img src="/images/pig.png" /> to redeem rewards
            </Typography>
          </div>
        </div>
        <div className="giftCardOptionsContainer">
          {campaign?.denomination.map((denomination: GiftCardDenomination, index: number) => (
            <Alert
              key={index}
              severity="info"
              icon={false}
              onClick={() => showConfirmOrderModal(denomination.value)}
              className="giftCardOptionAlert"
              data-denomination={denomination.value}
            >
              <div className="giftCardContainer">
                <div className="giftCardContent">
                  <img className="giftCardIcon" src="/images/giftBox.png" />
                  <Typography variant="h4" className="title">
                    Redeem ${denomination.value} USD
                  </Typography>
                </div>
                <div className="giftCardPrice">
                  <ValueFrame
                    value={PIGGY_BANKS_MULTIPLIER(denomination.value)}
                    isPig={true}
                    isModal={false}
                    style={styles.valueFrame(
                      (user?.piggyBanks ?? 0) < PIGGY_BANKS_MULTIPLIER(denomination.value)
                    )}
                  />
                </div>
              </div>
            </Alert>
          ))}
        </div>
      </Container>
    </>
  )
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    alignItems: 'center',
    padding: '16px 16px 0 16px',
  },
  image: {
    width: '147px',
  },
  valueFrame: (ineligiblePiggyBanksValue: boolean) => ({
    ...(ineligiblePiggyBanksValue && {
      background: 'linear-gradient(#fff9f3, #fff9f3) padding-box, #94A3B8 border-box',
      opacity: 0.6,
    }),
  }),
}

export default ContentWithCampaign
